import { ALL_PERMISSIONS, type Permission } from './permissions.js';

export interface RoleDefinition {
  id: string;
  name: Record<string, string>;
  permissions: Permission[];
  /** System roles can be edited but not deleted. */
  system: boolean;
}

const VIEW_BASE: Permission[] = ['survey.view', 'finding.view', 'template.view', 'masterdata.view', 'reports.view'];

export const DEFAULT_ROLES: RoleDefinition[] = [
  {
    id: 'system-admin',
    name: { en: 'System Admin', es: 'Administrador del sistema', pt: 'Administrador do sistema' },
    permissions: [...ALL_PERMISSIONS],
    system: true,
  },
  {
    id: 'hsse-director',
    name: {
      en: 'HSSE Director / Head of Safety',
      es: 'Director de HSSE / Jefe de Seguridad',
      pt: 'Diretor de HSSE / Chefe de Segurança',
    },
    permissions: [
      ...VIEW_BASE,
      'safety.command',
      'event.report',
      'event.view',
      'event.view_detail',
      'event.triage',
      'event.close',
      'insights.view',
      'intel.manage',
      'survey.review',
      'survey.schedule',
      'finding.manage',
      'finding.close',
      'data.export',
      'audit.view',
      'users.view',
    ],
    system: true,
  },
  {
    id: 'form-designer',
    name: { en: 'Form Designer', es: 'Diseñador de formularios', pt: 'Designer de formulários' },
    permissions: ['template.view', 'template.design', 'template.publish', 'masterdata.view'],
    system: true,
  },
  {
    id: 'reporter',
    name: { en: 'Reporter', es: 'Reportador', pt: 'Relator' },
    permissions: ['survey.view', 'survey.create', 'finding.view', 'template.view', 'masterdata.view', 'event.report'],
    system: true,
  },
  {
    // Frontline officers report hazards and incidents and see their own reports, nothing else (ADR-0029).
    id: 'frontline-officer',
    name: { en: 'Frontline Officer', es: 'Oficial de primera línea', pt: 'Oficial da linha da frente' },
    permissions: ['event.report'],
    system: true,
  },
  {
    // Regional and global leaders: trends without site or customer identities above the identification level (ADR-0030).
    id: 'regional-insights',
    name: { en: 'Regional Insights', es: 'Análisis regional', pt: 'Análise regional' },
    permissions: ['insights.view'],
    system: true,
  },
  {
    id: 'analyst',
    name: { en: 'Analyst', es: 'Analista', pt: 'Analista' },
    permissions: [
      ...VIEW_BASE,
      'safety.command',
      'survey.review',
      'survey.takeover',
      'finding.manage',
      'finding.close',
      'event.report',
      'event.view',
      'event.view_detail',
      'event.triage',
      'insights.view',
    ],
    system: true,
  },
  {
    id: 'account-manager',
    name: { en: 'Account Manager', es: 'Gerente de cuenta', pt: 'Gerente de conta' },
    permissions: [...VIEW_BASE, 'survey.communicate', 'finding.manage'],
    system: true,
  },
  {
    id: 'manager',
    name: { en: 'Manager / Viewer', es: 'Gerente / Consulta', pt: 'Gerente / Consulta' },
    permissions: [...VIEW_BASE, 'safety.command', 'survey.schedule', 'data.export', 'users.view', 'event.report', 'event.view', 'insights.view', 'intel.manage', 'catalogue.manage'],
    system: true,
  },
  {
    id: 'auditor',
    name: { en: 'Auditor', es: 'Auditor', pt: 'Auditor' },
    permissions: [...VIEW_BASE, 'users.view', 'audit.view', 'data.export', 'event.view', 'event.view_detail', 'insights.view'],
    system: true,
  },
];
